import React, { useEffect, useState } from 'react';
// Api
import client from '../../../api/client';
// Constants
import {
  GET_ALL_CONTACT_FROMS_API,
  DELETE_CONTACT_FORM_API,
  DELETE_ALL_CONTACT_FORM_API,
} from '../../../utils/ApiRoutes';
import { CompanyName } from '../../../utils/Constants';
// Components
import ConfirmModal from '../../modals/ConfirmModal';
import ContactFormItem from './ContactFormItem';
import LoadingSpinner from '../../utils/LoadingSpinner';
import { useUser } from '../../../context/UserContext';

function ContactFormAdminPageMainContainer() {
  const { contactForms, setContactForms } = useUser()
  
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [modalContent, setModalContent] = useState({ header: '', message: '' });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchContactForms();
  }, []);

  const fetchContactForms = () => {
    setIsLoading(true);

    client
      .get(GET_ALL_CONTACT_FROMS_API, true)
      .then((res) => {
        if (res?.data?.contactForms) {
          setContactForms(res.data.contactForms);
        } else {
          console.error('No contact forms found');
          setContactForms([]);
        }
      })
      .catch((err) => {
        console.error('Unable to retrieve contact form data', err);
        setContactForms([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const confirmAction = ({ action, header, message }) => {
    setPendingAction(() => action);
    setModalContent({ header, message });
    setModalOpen(true);
  };

  const handleModalConfirm = () => {
    if (pendingAction) pendingAction();
    setModalOpen(false);
  };

  const handleDelete = (id) => {
    confirmAction({
      header: 'Delete Contact Form',
      message: 'Are you sure you want to delete this contact form?',
      action: () => {
        setIsLoading(true);

        client
          .delete(`${DELETE_CONTACT_FORM_API}/${id}`, true)
          .then(() => {
            setContactForms((prev) => prev.filter((form) => form.id !== id));
          })
          .catch((err) => {
            console.error('Failed to delete contact form', err);
          })
          .finally(() => {
            setIsLoading(false);
          });
      },
    });
  };

  const handleDeleteAll = () => {
    confirmAction({
      header: 'Delete All Contact Forms',
      message:
        'Are you sure you want to delete all contact forms? This cannot be undone.',
      action: () => {
        setIsLoading(true);

        client
          .delete(DELETE_ALL_CONTACT_FORM_API, true)
          .then(() => {
            setContactForms([]);
          })
          .catch((err) => {
            console.error('Failed to delete all contact forms', err);
          })
          .finally(() => {
            setIsLoading(false);
          });
      },
    });
  };

  return (
    <main
      role='main'
      className='grid w-full h-full py-8 sm:py-10'
      aria-label='Admin contact form page'
    >
      <section className='grid w-full'>
        {/* Centered page container – grid, no margins */}
        <div className='grid grid-rows-reg w-full max-w-5xl justify-self-center gap-y-6 px-4 sm:px-6 lg:px-10'>
          {/* Header */}
          <section
            className='grid w-full bg-colour2 text-colour1 border rounded-sm shadow-sm px-3 sm:px-4 lg:px-6 py-3'
            aria-label='Contact form admin header'
          >
            <div className='grid items-center grid-cols-[minmax(0,1fr)_auto] gap-x-4'>
              <div className='grid'>
                <h1 className='text-base sm:text-lg md:text-xl lg:text-2xl font-semibold tracking-wide uppercase'>
                  Contact Forms
                </h1>
                <p className='text-xs sm:text-sm text-colour7'>
                  Review and manage contact submissions for {CompanyName}.
                </p>
              </div>

              {!isLoading && contactForms.length > 0 && (
                <div className='grid grid-flow-col auto-cols-max gap-x-2 justify-end items-center'>
                  <button
                    onClick={handleDeleteAll}
                    className='px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold tracking-wide uppercase rounded-sm bg-red-600 hover:bg-red-700 text-colour1 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-colour2'
                    aria-describedby='delete-all-description'
                  >
                    Delete All
                  </button>
                  <span id='delete-all-description' className='sr-only'>
                    Deletes all stored contact form submissions permanently
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* Contact forms list */}
          <section
            className='grid w-full bg-colour2 border border-colour5 rounded-sm shadow-sm p-3 sm:p-4 lg:p-6'
            aria-label={`List of submitted contact forms for ${CompanyName}`}
          >
            {isLoading ? (
              <section
                className='grid items-center justify-center h-full w-full py-10 sm:py-14 border border-solid border-colour5 rounded-sm gap-y-4'
                role='status'
                aria-live='polite'
              >
                <div className='grid gap-y-2 mx-auto'>
                  <p className='text-sm sm:text-base text-colour7 text-center'>
                    Loading contact forms…
                  </p>
                  <div className='grid mx-auto'>
                    <LoadingSpinner lg={true} />
                  </div>
                </div>
              </section>
            ) : contactForms.length === 0 ? (
              <section
                className='grid items-center justify-center h-full w-full py-10 sm:py-14 border border-solid border-colour5 rounded-sm'
                role='status'
                aria-live='polite'
              >
                <p className='text-sm sm:text-base text-colour7 text-center'>
                  No contact forms found yet.
                </p>
              </section>
            ) : (
              <ul className='grid gap-y-3 sm:gap-y-4 bg-colour5 h-fit p-1'>
                {contactForms.map((form, index) => (
                  <li key={form.id}>
                    <ContactFormItem
                      id={index}
                      form={form}
                      handleDelete={handleDelete}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </section>

      {/* Confirm Modal */}
      {modalOpen && (
        <ConfirmModal
          onClose={() => setModalOpen(false)}
          onConfirm={handleModalConfirm}
          header={modalContent.header}
          message={modalContent.message}
        />
      )}
    </main>
  );
}

export default ContactFormAdminPageMainContainer;
